import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import { AuthProvider, AuthProviderProps } from 'react-oidc-context';
import { WebStorageStateStore, type User, type UserProfile } from 'oidc-client-ts';
import { ConfirmProvider } from 'material-ui-confirm';
import { MaterialDesignContent } from 'notistack';
import { CookiesProvider } from 'react-cookie';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box, IconButton, styled } from '@mui/material';
import { Close, Error, CheckCircle, Warning, Info } from '@mui/icons-material';
import { SnackbarKey, SnackbarProvider, useSnackbar } from 'notistack';
import { queryDefaults, baseApiUrl, baseAppUrl, getDomainPattern } from '../../data_sources/constants';
import { translate as t } from 'hooks/useTranslations';
import AppProvider, { REDIR_BLACKLIST } from './context';
import ScrollToTop from './scrollToTop';
import Portal from './Portal';
import AuthCallback from './AuthCallback';
import Login from './Login';
import CorsValidator from './CorsValidator';
import Citations from './Prototypes/Citations';
import Translations from './Prototypes/Translations';
import TTS from './tools/TTS';
import { SearchProvider } from './Portal/Policy/SearchManuals/context';
import secureLs, { secureStorage } from '../../utils/secureStorage';
import AuthUtil from 'utils/AuthUtil';
import { loadAllTranslations } from '../../translations/loadTranslations';

const queryClient = new QueryClient(queryDefaults);

function SnackbarCloseButton({ snackbarKey }: { snackbarKey: SnackbarKey }) {
  const { closeSnackbar } = useSnackbar();

  return (
    <IconButton onClick={() => closeSnackbar(snackbarKey)}>
      <Close sx={{ color: '#FFF' }} />
    </IconButton>
  );
}

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent .MuiSvgIcon-root': {
    marginRight: 8,
  },
  '&.notistack-MuiContent-success': {
    backgroundColor: '#2D7738',
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: '#970C0C',
  },
}));

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userOrgId, setUserOrgId] = useState<string>('');

  const reqId = uuidv4();
  const oidcConfig: AuthProviderProps = {
    authority: baseApiUrl() || '',
    client_id: process.env.REACT_APP_CLIENT_ID || '',
    redirect_uri: `${baseAppUrl()}${getDomainPattern() === 'old' ? '' : '/admin'}/auth-callback.html`,
    post_logout_redirect_uri: `${baseAppUrl()}${getDomainPattern() === 'old' ? '' : '/admin'}/login`,
    scope: ['openid', 'profile', 'email', 'phone'].join(' '),
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: secureStorage }),
    extraQueryParams: {
      req: reqId,
      client_id: process.env.REACT_APP_CLIENT_ID || '',
    },
    extraTokenParams: {
      req: reqId,
    },
    onSigninCallback: (user: any) => {
      // Clear the signout cookie on successful signin
      AuthUtil.clearSignOutCookie();

      if (!!user) {
        const oidcUser = user as User;
        secureStorage.setItem('lexat', oidcUser?.access_token || '');

        // Only set localStorage if not in signout process
        if (AuthUtil.shouldAllowStorageOperations()) {
          localStorage.setItem('phx-currentUserName', oidcUser?.profile['cognito:username']?.toString() || '');
          const orgId = oidcUser?.profile['custom:orgId']?.toString();
          if (orgId) {
            // Always set phx-currentOrganizationId to phx-userOrgId if userOrgId is not lexipoladminstaffuuid
            if (orgId !== 'lexipoladminstaffuuid') {
              localStorage.setItem('phx-currentOrganizationId', orgId);
            }
            setUserOrgId(orgId);
            localStorage.setItem('phx-userOrgId', orgId);
          }
        }
        // Navigation will be handled by the AuthCallback component
      } else {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    },
  };

  useEffect(() => {
    // Load all translations when the app starts
    loadAllTranslations();

    window.addEventListener('error', (e) => {
      if (e.message === 'ResizeObserver loop limit exceeded' || e.message === 'Script error.') {
        const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    });
  }, []);

  const queryProviderToken = AuthUtil.getToken() || 'anon';
  const isTokenInValid = queryProviderToken === 'anon';

  return (
    <AuthProvider {...oidcConfig}>
      <QueryClientProvider
        client={queryClient}
        key={AuthUtil.getToken() || 'anon'}
      >
        <AppProvider
          userOrgId={userOrgId}
          isTokenValid={!isTokenInValid}
        >
          <ConfirmProvider>
            <CookiesProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <SnackbarProvider
                  action={(snackbarKey) => <SnackbarCloseButton snackbarKey={snackbarKey} />}
                  iconVariant={{
                    success: <CheckCircle />,
                    error: <Error />,
                    warning: <Warning />,
                    info: <Info />,
                  }}
                  Components={{
                    success: StyledMaterialDesignContent,
                    error: StyledMaterialDesignContent,
                  }}
                  style={{ zIndex: 9999999 }}
                />
                <SearchProvider>
                  <ScrollToTop />
                  <Box component='main'>
                    <Routes>
                      <Route
                        key='root'
                        path='/'
                        element={<Navigate to='/admin/dashboard' />}
                      />
                      <Route
                        key='login'
                        path='/admin/login/*'
                        element={<Login />}
                      />
                      <Route
                        key='old_callback'
                        path='/auth-callback.html'
                        element={<AuthCallback />}
                      />
                      <Route
                        key='callback'
                        path='/admin/auth-callback.html'
                        element={<AuthCallback />}
                      />
                      <Route
                        key='dashboard'
                        path='/dashboard/*'
                        element={<Navigate to='/admin/dashboard' />}
                      />
                      <Route
                        key='portal_root'
                        path='/portal/*'
                        element={
                          <Navigate to={`/admin${location.pathname.replace('/portal', '')}${location.search}`} />
                        }
                      />
                      <Route
                        key='admin_root'
                        path='/admin/*'
                        element={<Portal />}
                      />
                      <Route
                        key='cors_validator'
                        path='/cors-validator/*'
                        element={<CorsValidator />}
                      />
                      <Route
                        key='prototype_citations'
                        path='/prototypes/citations/*'
                        element={<Citations />}
                      />
                      <Route
                        key='prototype_translations'
                        path='/prototypes/translations/*'
                        element={<Translations />}
                      />
                      <Route
                        key='tools_tts'
                        path='/admin/tools/tts/*'
                        element={<TTS />}
                      />
                      <Route
                        path='*'
                        element={<Navigate to='/admin/login' />}
                      />
                    </Routes>
                  </Box>
                </SearchProvider>
              </LocalizationProvider>
            </CookiesProvider>
          </ConfirmProvider>
        </AppProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}
